import React, { useEffect, useState } from 'react';
import { Save, Eye, Edit3, ChevronDown } from 'lucide-react';
import { mockInstructionsByAgent, templatesPremessa, templatesLogica, templatesOutput } from '../../data/mockData';
import { InstructionSet, InstructionTemplate, VersionedSection } from '../../types';
import { useAgent } from '../../contexts/AgentContext';
import { AppCard } from '../base/AppCard';
import { AppButton } from '../base/AppButton';
import { AppTextarea } from '../base/AppTextarea';
import { Toast } from '../Toast';
import { InstructionVersionModal } from '../modals/InstructionVersionModal';
import * as mockApi from '../../services/mockApi';
type SectionKey = 'premessa' | 'logica' | 'output';
export function AgentIstruzioniTab() {
  const {
    activeAgentId
  } = useAgent();
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [instructions, setInstructions] = useState<InstructionSet | null>(null);
  const [editorContent, setEditorContent] = useState({
    premessa: '',
    logica: '',
    output: ''
  });
  // Modal state
  const [savingSection, setSavingSection] = useState<SectionKey | null>(null);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  useEffect(() => {
    const data = mockInstructionsByAgent[activeAgentId] || mockInstructionsByAgent['default'];
    const deepCopy = JSON.parse(JSON.stringify(data));
    setInstructions(deepCopy);
    setEditorContent({
      premessa: getCurrentContent(deepCopy.premessa),
      logica: getCurrentContent(deepCopy.logica),
      output: getCurrentContent(deepCopy.output)
    });
  }, [activeAgentId]);
  const getCurrentContent = (section: VersionedSection) => {
    const version = section.versions.find(v => v.id === section.currentVersionId);
    return version ? version.content : '';
  };
  const handleTemplateChange = (section: SectionKey, templateId: string) => {
    let templates: InstructionTemplate[] = [];
    if (section === 'premessa') templates = templatesPremessa;
    if (section === 'logica') templates = templatesLogica;
    if (section === 'output') templates = templatesOutput;
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEditorContent(prev => ({
        ...prev,
        [section]: template.content
      }));
    }
  };
  const handleVersionChange = (section: SectionKey, versionId: string) => {
    if (!instructions) return;
    const newInstructions = {
      ...instructions
    };
    newInstructions[section].currentVersionId = versionId;
    setInstructions(newInstructions);
    const version = newInstructions[section].versions.find(v => v.id === versionId);
    if (version) {
      setEditorContent(prev => ({
        ...prev,
        [section]: version.content
      }));
    }
  };
  const handleOpenSaveModal = (section: SectionKey) => {
    setSavingSection(section);
  };
  const handleSaveVersion = async (versionName: string) => {
    if (!instructions || !savingSection) return;
    const currentContent = editorContent[savingSection];
    try {
      const newVersion = await mockApi.saveInstructionVersion(activeAgentId, savingSection, {
        versionName,
        content: currentContent
      });
      // Update local state
      const newInstructions = {
        ...instructions
      };
      newInstructions[savingSection].versions.push({
        id: newVersion.id,
        label: newVersion.versionName,
        content: newVersion.content,
        createdAt: newVersion.timestamp
      });
      newInstructions[savingSection].currentVersionId = newVersion.id;
      setInstructions(newInstructions);
      setToast({
        message: `Versione "${versionName}" salvata`,
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nel salvataggio della versione',
        type: 'error'
      });
    }
  };
  if (!instructions) return <div data-id="element-701">Caricamento...</div>;
  return <div className="space-y-8" data-id="element-702">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-703" />}

      {/* Instructions Editor */}
      <div data-id="element-704">
        <div className="flex items-center justify-between mb-6" data-id="element-705">
          <div data-id="element-706">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-707">
              Istruzioni prompt
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-708">
              Definisci premessa, logica e formato output
            </p>
          </div>

          <div className="flex items-center bg-gray-100 dark:bg-dark-subtle p-1 rounded-lg" data-id="element-709">
            <button onClick={() => setMode('edit')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-180 ${mode === 'edit' ? 'bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 shadow-sm dark:shadow-dark-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'}`} data-id="element-710">
              <Edit3 className="w-4 h-4 mr-2" data-id="element-711" />
              Modifica
            </button>
            <button onClick={() => setMode('preview')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-180 ${mode === 'preview' ? 'bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 shadow-sm dark:shadow-dark-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'}`} data-id="element-712">
              <Eye className="w-4 h-4 mr-2" data-id="element-713" />
              Preview
            </button>
          </div>
        </div>

        {mode === 'edit' ? <div className="space-y-6" data-id="element-714">
            <SectionEditor title="Premessa" sectionKey="premessa" content={editorContent.premessa} versions={instructions.premessa.versions} currentVersionId={instructions.premessa.currentVersionId} templates={templatesPremessa} onChange={val => setEditorContent(prev => ({
          ...prev,
          premessa: val
        }))} onTemplateSelect={id => handleTemplateChange('premessa', id)} onVersionSelect={id => handleVersionChange('premessa', id)} onSave={() => handleOpenSaveModal('premessa')} data-id="element-715" />

            <SectionEditor title="Logica" sectionKey="logica" content={editorContent.logica} versions={instructions.logica.versions} currentVersionId={instructions.logica.currentVersionId} templates={templatesLogica} onChange={val => setEditorContent(prev => ({
          ...prev,
          logica: val
        }))} onTemplateSelect={id => handleTemplateChange('logica', id)} onVersionSelect={id => handleVersionChange('logica', id)} onSave={() => handleOpenSaveModal('logica')} data-id="element-716" />

            <SectionEditor title="Output" sectionKey="output" content={editorContent.output} versions={instructions.output.versions} currentVersionId={instructions.output.currentVersionId} templates={templatesOutput} onChange={val => setEditorContent(prev => ({
          ...prev,
          output: val
        }))} onTemplateSelect={id => handleTemplateChange('output', id)} onVersionSelect={id => handleVersionChange('output', id)} onSave={() => handleOpenSaveModal('output')} data-id="element-717" />
          </div> : <AppCard padding="large" data-id="element-718">
            <div className="space-y-8 font-mono text-sm bg-gray-50 dark:bg-dark-subtle p-6 rounded-xl border border-gray-100 dark:border-dark-medium" data-id="element-719">
              <div data-id="element-720">
                <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 border-b border-gray-200 dark:border-dark-medium pb-2" data-id="element-721">
                  ### Premessa
                </h3>
                <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200" data-id="element-722">
                  {editorContent.premessa}
                </div>
              </div>

              <div data-id="element-723">
                <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 border-b border-gray-200 dark:border-dark-medium pb-2" data-id="element-724">
                  ### Logica
                </h3>
                <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200" data-id="element-725">
                  {editorContent.logica}
                </div>
              </div>

              <div data-id="element-726">
                <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 border-b border-gray-200 dark:border-dark-medium pb-2" data-id="element-727">
                  ### Output
                </h3>
                <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200" data-id="element-728">
                  {editorContent.output}
                </div>
              </div>
            </div>
          </AppCard>}
      </div>

      {/* Version Save Modal */}
      {savingSection && <InstructionVersionModal isOpen={!!savingSection} section={savingSection} content={editorContent[savingSection]} onClose={() => setSavingSection(null)} onSave={handleSaveVersion} data-id="element-729" />}
    </div>;
}
interface SectionEditorProps {
  title: string;
  sectionKey: SectionKey;
  content: string;
  versions: {
    id: string;
    label: string;
  }[];
  currentVersionId: string;
  templates: InstructionTemplate[];
  onChange: (value: string) => void;
  onTemplateSelect: (id: string) => void;
  onVersionSelect: (id: string) => void;
  onSave: () => void;
}
function SectionEditor({
  title,
  content,
  versions,
  currentVersionId,
  templates,
  onChange,
  onTemplateSelect,
  onVersionSelect,
  onSave
}: SectionEditorProps) {
  return <AppCard padding="none" data-id="element-730">
      <div className="bg-gray-50 dark:bg-dark-subtle px-6 py-4 border-b border-gray-100 dark:border-dark-medium flex justify-between items-center" data-id="element-731">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-732">
          {title}
        </h3>

        <div className="flex items-center space-x-3" data-id="element-733">
          <div className="relative" data-id="element-734">
            <select onChange={e => {
            if (e.target.value) {
              onTemplateSelect(e.target.value);
              e.target.value = '';
            }
          }} className="appearance-none bg-white dark:bg-dark-surface border border-gray-300/80 dark:border-dark-medium text-gray-700 dark:text-gray-300 py-2 pl-3 pr-9 rounded-lg text-sm focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900 focus:border-blue-500 dark:focus:border-blue-500 cursor-pointer transition-all duration-180" data-id="element-735">
              <option value="" data-id="element-736">Template...</option>
              {templates.map(t => <option key={t.id} value={t.id} data-id="element-737">
                  {t.label}
                </option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 absolute right-2.5 top-2.5 pointer-events-none" data-id="element-738" />
          </div>

          <div className="relative" data-id="element-739">
            <select value={currentVersionId} onChange={e => onVersionSelect(e.target.value)} className="appearance-none bg-white dark:bg-dark-surface border border-gray-300/80 dark:border-dark-medium text-gray-700 dark:text-gray-300 py-2 pl-3 pr-9 rounded-lg text-sm focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900 focus:border-blue-500 dark:focus:border-blue-500 cursor-pointer min-w-[140px] transition-all duration-180" data-id="element-740">
              {versions.map(v => <option key={v.id} value={v.id} data-id="element-741">
                  {v.label}
                </option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 absolute right-2.5 top-2.5 pointer-events-none" data-id="element-742" />
          </div>
        </div>
      </div>

      <div className="p-6" data-id="element-743">
        <AppTextarea value={content} onChange={e => onChange(e.target.value)} placeholder={`Inserisci qui le istruzioni per ${title.toLowerCase()}...`} rows={8} className="font-mono text-sm" data-id="element-744" />

        <div className="mt-4 flex justify-end" data-id="element-745">
          <AppButton variant="secondary" size="sm" icon={<Save className="w-4 h-4" data-id="element-747" />} onClick={onSave} data-id="element-746">
            Salva come nuova versione
          </AppButton>
        </div>
      </div>
    </AppCard>;
}